/**
 * Shop by Notes Filtering Functionality
 */

class ShopByNotes {
  constructor() {
    this.sections = document.querySelectorAll('.shop-by-notes[data-section-id]');
    this.init();
  }

  init() {
    this.sections.forEach((section) => {
      const sectionId = section.dataset.sectionId;
      const filterButtons = section.querySelectorAll('.shop-by-notes__filter-btn[data-section-id="' + sectionId + '"]');
      const productGroups = section.querySelectorAll('.shop-by-notes__product-group');
      
      // Set initial active state
      const defaultFilter = section.querySelector('.shop-by-notes__filter-btn.active');
      if (defaultFilter) {
        const filterValue = defaultFilter.dataset.filter;
        this.showFilterGroup(section, filterValue);
      }

      // Update product counts
      this.updateProductCounts(section);

      // Add click handlers
      filterButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const filterValue = button.dataset.filter;
          this.handleFilterClick(section, filterValue, button, filterButtons);
        });
      });
      
      // Initialize scroll indicators for product grids on mobile
      this.initScrollIndicators(section);
      
      // Initialize scroll buttons
      this.initScrollButtons(section);
    });
  }

  initScrollIndicators(section) {
    const grids = section.querySelectorAll('.shop-by-notes__grid');
    
    grids.forEach((grid) => {
      const updateScrollIndicator = () => {
        const isScrollable = grid.scrollWidth > grid.clientWidth;
        const isScrolledToEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 10; // 10px threshold
        
        if (isScrollable && !isScrolledToEnd) {
          grid.classList.remove('scrolled-to-end');
        } else {
          grid.classList.add('scrolled-to-end');
        }
      };

      // Check on load and resize
      updateScrollIndicator();
      window.addEventListener('resize', updateScrollIndicator);
      
      // Check on scroll
      grid.addEventListener('scroll', updateScrollIndicator);
      
      // Also check when filter changes (new content might be loaded)
      const observer = new MutationObserver(updateScrollIndicator);
      observer.observe(grid, { childList: true, subtree: true });
      
      // Re-check when filter group changes
      const productGroups = section.querySelectorAll('.shop-by-notes__product-group');
      productGroups.forEach((group) => {
        const groupObserver = new MutationObserver(() => {
          setTimeout(updateScrollIndicator, 100); // Small delay to allow DOM update
        });
        groupObserver.observe(group, { 
          attributes: true, 
          attributeFilter: ['class'],
          childList: true 
        });
      });
    });
  }

  initScrollButtons(section) {
    const scrollButtons = section.querySelectorAll('.shop-by-notes__scroll-button');
    
    scrollButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const productGroup = button.closest('.shop-by-notes__product-group');
        const grid = productGroup ? productGroup.querySelector('.shop-by-notes__grid') : null;
        if (!grid) return;
        
        // Calculate scroll amount: scroll by ~2 cards on desktop
        const cardWidth = grid.querySelector('.grid__item')?.offsetWidth || 300;
        const gap = 32; // 2rem gap
        const scrollAmount = (cardWidth + gap) * 2;
        
        let targetScroll;
        if (button.classList.contains('shop-by-notes__scroll-button--prev')) {
          targetScroll = grid.scrollLeft - scrollAmount;
        } else {
          targetScroll = grid.scrollLeft + scrollAmount;
        }
        
        grid.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
        
        // Update button visibility after scroll completes
        setTimeout(() => {
          this.updateScrollButtonVisibility(productGroup, grid);
        }, 500);
      });
    });
    
    // Initialize button visibility for all grids
    section.querySelectorAll('.shop-by-notes__product-group').forEach((group) => {
      const grid = group.querySelector('.shop-by-notes__grid');
      if (grid) {
        // Initial check
        setTimeout(() => this.updateScrollButtonVisibility(group, grid), 100);
        
        // Update on scroll
        grid.addEventListener('scroll', () => {
          this.updateScrollButtonVisibility(group, grid);
        });
        
        // Update on resize
        window.addEventListener('resize', () => {
          this.updateScrollButtonVisibility(group, grid);
        });
      }
    });
  }

  updateScrollButtonVisibility(productGroup, grid) {
    const prevButton = productGroup.querySelector('.shop-by-notes__scroll-button--prev');
    const nextButton = productGroup.querySelector('.shop-by-notes__scroll-button--next');
    
    const isScrollable = grid.scrollWidth > grid.clientWidth;
    const isScrolledToStart = grid.scrollLeft <= 10;
    const isScrolledToEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 10;
    
    if (prevButton) {
      if (isScrollable && !isScrolledToStart) {
        prevButton.classList.remove('hidden');
      } else {
        prevButton.classList.add('hidden');
      }
    }
    
    if (nextButton) {
      if (isScrollable && !isScrolledToEnd) {
        nextButton.classList.remove('hidden');
      } else {
        nextButton.classList.add('hidden');
      }
    }
  }

  handleFilterClick(section, filterValue, clickedButton, allButtons) {
    // Update active state
    allButtons.forEach((btn) => {
      btn.classList.remove('active');
    });
    clickedButton.classList.add('active');

    // Show/hide product groups
    this.showFilterGroup(section, filterValue);

    // Smooth scroll to products if needed
    const productsContainer = section.querySelector('[data-products-container]');
    if (productsContainer) {
      const rect = productsContainer.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight) {
        productsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  showFilterGroup(section, filterValue) {
    const productGroups = section.querySelectorAll('.shop-by-notes__product-group');
    
    productGroups.forEach((group) => {
      const groupFilter = group.dataset.filterGroup;
      
      if (groupFilter === filterValue) {
        group.classList.remove('hidden');
        // Trigger animation
        group.style.animation = 'none';
        setTimeout(() => {
          group.style.animation = 'fadeIn 0.4s ease-in-out';
        }, 10);
        
        // Reset scroll position and update button visibility
        const grid = group.querySelector('.shop-by-notes__grid');
        if (grid) {
          grid.scrollLeft = 0;
          setTimeout(() => {
            this.updateScrollButtonVisibility(group, grid);
          }, 50);
        }
      } else {
        group.classList.add('hidden');
      }
    });
  }

  updateProductCounts(section) {
    const productGroups = section.querySelectorAll('.shop-by-notes__product-group');
    const filterButtons = section.querySelectorAll('.shop-by-notes__filter-btn');
    
    productGroups.forEach((group) => {
      const groupFilter = group.dataset.filterGroup;
      const productCount = group.dataset.productCount || group.querySelectorAll('.product-card-enhanced').length;
      
      filterButtons.forEach((button) => {
        if (button.dataset.filter === groupFilter) {
          const countElement = button.querySelector('.shop-by-notes__filter-count');
          if (countElement) {
            countElement.textContent = `(${productCount})`;
          }
        }
      });
    });
    
    // Update "All" filter count if it exists
    const allButton = section.querySelector('.shop-by-notes__filter-btn[data-filter="all"]');
    if (allButton) {
      const allGroup = section.querySelector('.shop-by-notes__product-group[data-filter-group="all"]');
      if (allGroup) {
        const allCount = allGroup.dataset.productCount || allGroup.querySelectorAll('.product-card-enhanced').length;
        const allCountElement = allButton.querySelector('.shop-by-notes__filter-count');
        if (allCountElement) {
          allCountElement.textContent = `(${allCount})`;
        }
      }
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ShopByNotes();
  });
} else {
  new ShopByNotes();
}

